import { defineElement, FC, onDidLoad, useProp } from '@atomify/hooks';
import { h } from '@atomify/jsx';
import { Button, ButtonA } from '@atoms/button/button';
import {
    extendedArea,
    ExtendedConstructionNumber,
    ExtendedProjectPhase,
} from '@bpd-library/utilities';
import { isSubscribedToProjectPhase, subscriptionStore } from '@store/subscription/store';
import { getUserBuyerStatus, isLoggedIn } from '@store/user';

import { ctaLabelForAction, getCtaFlow, HouseDetailpageCtaAction } from './utilities/cta-helpers';

const CTA_IS_LOADING_CLASS = 'is-loading';

const HouseDetailpageCta: FC = ({ element }) => {
    const [projectPhaseId] = useProp<string>('projectPhaseId', '');
    const [propertyTypeId] = useProp<string>('propertyTypeId', '');
    const [constructionNumberId] = useProp<string | null>('constructionNumberId', null);
    const [housingOverviewUrl] = useProp<string | null>('housingOverviewUrl', null);
    const [startSaleUrl] = useProp<string | null>('startSaleUrl', null);

    let projectPhase: ExtendedProjectPhase | undefined;
    let constructionNumber: ExtendedConstructionNumber | undefined;

    subscriptionStore.subscribe(render, ['projectPhases']);

    onDidLoad(async () => {
        const data = await extendedArea.getData(window.__ENVIRONMENT_DETAILS__.areaId);
        projectPhase = data.projectPhases.find(({ id }) => id === projectPhaseId);
        constructionNumber = data.constructionNumbers.find(({ id }) => id === constructionNumberId);

        render();
    });

    async function render() {
        if (!projectPhase) return;

        const { action, flow } = getCtaFlow(projectPhase, constructionNumber, startSaleUrl) || {};
        if (!action) return;

        switch (flow) {
            case 'subscription': {
                await renderSubscriptionFlow(action);
                break;
            }
            case 'vkk': {
                await renderVkkFlow(action);
                break;
            }
            case 'overview': {
                await renderBackToOverview();
                break;
            }
        }
    }

    async function renderSubscriptionFlow(action: HouseDetailpageCtaAction) {
        const completed = isSubscribedToProjectPhase(projectPhaseId);
        const label = await ctaLabelForAction(action, completed);

        const dataAttributes = {
            'property-type-id': `${propertyTypeId}`,
            ...(constructionNumberId && {
                'construction-number-id': `${constructionNumberId}`,
            }),
        };

        element.innerHTML = '';
        element.append(
            <Button
                label={label}
                disabled={completed}
                loadableModal="lead-form"
                attr={{ 'data-attributes': JSON.stringify(dataAttributes) }}
            />,
        );
    }

    async function renderVkkFlow(action: HouseDetailpageCtaAction) {
        const label = await ctaLabelForAction(action, false);
        const cta: HTMLAnchorElement = <ButtonA label={label} url={startSaleUrl} hasLoading />;

        element.innerHTML = '';
        element.append(cta);

        if (isLoggedIn()) {
            cta.classList.add(CTA_IS_LOADING_CLASS);

            const buyerStatus = await getUserBuyerStatus(constructionNumberId!);
            if (buyerStatus && buyerStatus !== 'none') {
                const span = cta.querySelector<HTMLSpanElement>('.button__label')!;
                span.innerHTML = await ctaLabelForAction(action, buyerStatus);

                cta.setAttribute('disabled', 'disabled');
            }
            cta.classList.remove(CTA_IS_LOADING_CLASS);
        }
    }

    async function renderBackToOverview() {
        element.innerHTML = '';
        element.append(
            <ButtonA
                label={await ctaLabelForAction('returnToOverview')}
                url={housingOverviewUrl}
            />,
        );
    }

    return <span></span>;
};

HouseDetailpageCta.props = {
    projectPhaseId: {
        type: String,
    },
    propertyTypeId: {
        type: String,
    },
    constructionNumberId: {
        type: String,
    },
    housingOverviewUrl: {
        type: String,
    },
    startSaleUrl: {
        type: String,
    },
};

defineElement('house-detailpage-cta', HouseDetailpageCta);
